import {
    Component, Input, Output, EventEmitter,
    AfterViewInit, OnDestroy, OnChanges, SimpleChanges,
    ViewChild, ElementRef, ChangeDetectorRef
} from '@angular/core';
import * as L from 'leaflet';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
    iconUrl: 'assets/leaflet/marker-icon.png',
    shadowUrl: 'assets/leaflet/marker-shadow.png',
});

const MAX_DECIMALS = 6;
const DEFAULT_CENTER: L.LatLngExpression = [46.603354, 1.888334];
const DEFAULT_ZOOM = 5;
const POSITIONED_ZOOM = 6;

@Component({
    selector: 'app-geopoint-widget',
    templateUrl: './geopoint-widget.component.html',
    styleUrls: ['./geopoint-widget.component.scss'],
})
export class GeopointWidgetComponent implements AfterViewInit, OnDestroy, OnChanges {
    @Input() lat: string | null = null;
    @Input() lng: string | null = null;
    @Input() disabled = false;
    @Input() mapReadonly = false;

    @Output() latChanged = new EventEmitter<string>();
    @Output() lngChanged = new EventEmitter<string>();
    @Output() onHistoryLat = new EventEmitter<void>();
    @Output() onHistoryLng = new EventEmitter<void>();

    @ViewChild('mapContainer') mapContainer!: ElementRef;

    latDisplay = '';
    lngDisplay = '';
    latError = '';
    lngError = '';

    private map: L.Map | null = null;
    private marker: L.Marker | null = null;

    constructor(private cdr: ChangeDetectorRef) {}

    ngAfterViewInit(): void {
        this.initMap();
        this.syncFromInputs();
        // Tiles may not render if map is initialized before container is visible
        setTimeout(() => this.map?.invalidateSize(), 200);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['lat'] || changes['lng']) {
            this.syncFromInputs();
        }
    }

    ngOnDestroy(): void {
        this.map?.remove();
    }

    onLatChange(raw: string): void {
        const result = this.parseCoordinate(raw, -90, 90);
        this.latError = result.error;
        if (result.error) { return; }
        this.latDisplay = result.formatted;
        this.latChanged.emit(result.formatted);
        this.updateMarker();
    }

    onLngChange(raw: string): void {
        const result = this.parseCoordinate(raw, -180, 180);
        this.lngError = result.error;
        if (result.error) { return; }
        this.lngDisplay = result.formatted;
        this.lngChanged.emit(result.formatted);
        this.updateMarker();
    }

    private syncFromInputs(): void {
        this.latDisplay = this.normalize(this.lat);
        this.lngDisplay = this.normalize(this.lng);
        this.latError = '';
        this.lngError = '';
        this.updateMarker();
    }

    private initMap(): void {
        if (!this.mapContainer) { return; }

        this.map = L.map(this.mapContainer.nativeElement, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            attributionControl: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | Tiles by OSM France',
        }).addTo(this.map);

        if (!this.disabled && !this.mapReadonly) {
            this.map.on('click', (e: L.LeafletMouseEvent) => {
                const lat = this.truncate(e.latlng.lat);
                const lng = this.truncate(e.latlng.lng);
                this.latDisplay = lat;
                this.lngDisplay = lng;
                this.latError = '';
                this.lngError = '';
                this.latChanged.emit(lat);
                this.lngChanged.emit(lng);
                this.updateMarker();
                this.cdr.detectChanges();
            });
        }
    }

    private updateMarker(): void {
        if (!this.map) { return; }

        const lat = parseFloat(this.latDisplay);
        const lng = parseFloat(this.lngDisplay);

        if (isNaN(lat) || isNaN(lng)) {
            this.marker?.remove();
            this.marker = null;
            this.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
            return;
        }

        const pos: L.LatLngExpression = [lat, lng];
        if (this.marker) {
            this.marker.setLatLng(pos);
        } else {
            this.marker = L.marker(pos).addTo(this.map);
        }
        this.map.setView(pos, POSITIONED_ZOOM);
    }

    private parseCoordinate(
        raw: string, min: number, max: number
    ): { formatted: string; error: string } {
        const trimmed = raw.trim();
        if (trimmed === '') {
            return { formatted: '', error: 'Ce champ est requis' };
        }

        if (trimmed.includes(',')) {
            return { formatted: raw, error: 'Utilisez un point (.) et non une virgule (,). Ex: 48.856600' };
        }

        if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
            return { formatted: raw, error: 'Format invalide (ex: 48.856600)' };
        }

        const num = parseFloat(trimmed);
        if (num < min || num > max) {
            return { formatted: raw, error: `Doit \u00eatre entre ${min} et ${max}` };
        }

        return { formatted: this.truncate(num), error: '' };
    }

    private truncate(n: number): string {
        return (Math.trunc(n * 1e6) / 1e6).toFixed(MAX_DECIMALS);
    }

    private normalize(val: string | null): string {
        if (!val || val.trim() === '') { return ''; }
        const num = parseFloat(val);
        return isNaN(num) ? val : this.truncate(num);
    }
}
